/**
 * The inventory on the Character (issue #11): adding and removing items, quantities, carry state,
 * containers, investiture, runes, precious materials, ammunition and coins. Pure functions over
 * the Character; what is structurally impossible is refused with a reason (holding a two-handed
 * weapon without two free hands, investing an eleventh item, a rune the item has no slot for),
 * read from the derived inventory view (`sheet.inventory`) the way the guide reads its
 * candidates from the sheet. Anything merely unwise is allowed.
 */
import type { Entry, IndexRecord } from "../../shared/snapshot-format";
import { Coins, type RawCoins } from "../engine/coins";
import type { InventoryView } from "../engine/inventory";
import { isMaterialGrade, materialGradesFor, materialsFor } from "../engine/materials-data";
import { isPhysicalType, usageDetails } from "../engine/physical";
import { ARMOR_PROPERTY_RUNES, WEAPON_PROPERTY_RUNES, propertyRuneSlots, prunePropertyRunes } from "../engine/runes-data";
import type { CarryType, Character, Inventory, InventoryItem, ItemMaterial, ItemRunes, TraitToggles } from "./model";

export interface InventoryChangeResult {
  character: Character;
  /** Why the change was refused (the Character is unchanged), or null. */
  blocked: string | null;
  /** The id of the item added or changed, when one was. */
  id?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function ok(character: Character, id?: string): InventoryChangeResult {
  return id === undefined ? { character, blocked: null } : { character, blocked: null, id };
}

function blocked(character: Character, reason: string): InventoryChangeResult {
  return { character, blocked: reason };
}

function withInventory(character: Character, inventory: Inventory): Character {
  return { ...character, inventory };
}

function withItems(character: Character, items: InventoryItem[]): Character {
  return withInventory(character, { ...character.inventory, items });
}

function itemOf(character: Character, id: string): InventoryItem | undefined {
  return character.inventory.items.find((i) => i.id === id);
}

function replaceItem(character: Character, next: InventoryItem): Character {
  return withItems(character, character.inventory.items.map((i) => (i.id === next.id ? next : i)));
}

/** Facts about an equipment Entry from the catalogue index, as the pickers see them. */
export interface ItemRecordFacts {
  uuid: string;
  name: string;
  type: string;
  level: number;
  rarity: string;
  traits: string[];
  remaster: boolean;
  publication: string;
}

export function itemRecordFacts(record: IndexRecord): ItemRecordFacts {
  return { uuid: record.u, name: record.n, type: record.t, level: record.l ?? 0, rarity: record.r, traits: record.tr, remaster: record.rm, publication: record.pub };
}

export interface ItemCandidateQuery {
  text: string;
  /** Restrict to these Entry types (weapon, armor, ...); every physical type and kits otherwise. */
  types?: string[];
  legacy: boolean;
  /** Include uncommon, rare and unique items. */
  uncommon: boolean;
  limit: number;
}

/** Equipment Entries a picker may offer: prefix matches on the name first, then substring matches, each in name order. */
export function itemCandidates(records: readonly IndexRecord[], q: ItemCandidateQuery): ItemRecordFacts[] {
  const needle = q.text.trim().toLowerCase();
  const types = q.types ?? null;
  const prefix: IndexRecord[] = [];
  const contains: IndexRecord[] = [];
  for (const r of records) {
    if (r.p !== "equipment-srd") continue;
    if (types ? !types.includes(r.t) : !(isPhysicalType(r.t) || r.t === "kit")) continue;
    if (!q.legacy && !r.rm) continue;
    if (!q.uncommon && r.r !== "common") continue;
    const name = r.n.toLowerCase();
    if (!needle) contains.push(r);
    else if (name.startsWith(needle)) prefix.push(r);
    else if (name.includes(needle)) contains.push(r);
  }
  const byName = (a: IndexRecord, b: IndexRecord): number => a.n.localeCompare(b.n);
  return [...prefix.sort(byName), ...contains.sort(byName)].slice(0, q.limit).map(itemRecordFacts);
}

/** How a freshly added Entry is carried (Foundry: getDefaultEquipStatus): worn, not in its slot, not invested, in no hand. */
export function defaultCarry(entry: Entry): Pick<InventoryItem, "carryType" | "handsHeld" | "inSlot" | "invested"> {
  const usage = usageDetails(entry.type === "armor" ? "wornarmor" : entry.type === "shield" ? "held-in-one-hand" : typeof (entry.system.usage as { value?: unknown } | undefined)?.value === "string" ? ((entry.system.usage as { value: string }).value) : "carried");
  const out: Pick<InventoryItem, "carryType" | "handsHeld" | "inSlot" | "invested"> = { carryType: "worn", handsHeld: 0 };
  if (usage.type === "worn" && usage.where) out.inSlot = false;
  if (entry.traits.includes("invested")) out.invested = false;
  return out;
}

export interface AddItemOptions {
  quantity?: number;
  /** The container to put it in. */
  container?: string | null;
  /** Whether that container stows its contents (the view says); assumed when not given. */
  stows?: boolean;
  /** Merge with an identical unequipped stack when the Entry stacks (consumables, ammunition, treasure, gear). */
  stack?: boolean;
  /** The Character's size, so gear is sized for a Large or Small Character (small counts as medium). */
  size?: string;
}

const STACKABLE_TYPES = new Set(["ammo", "consumable", "equipment", "treasure", "book"]);

/** Add a physical Entry (or a kit, expanded to its contents). */
export function addItem(character: Character, entry: Entry, options: AddItemOptions = {}): InventoryChangeResult {
  if (entry.type === "kit") return addKit(character, entry, options);
  if (!isPhysicalType(entry.type)) return blocked(character, `${entry.name} is not a physical item`);
  const quantity = Math.max(1, Math.trunc(options.quantity ?? (typeof entry.system.quantity === "number" && entry.system.quantity > 0 ? entry.system.quantity : 1)));
  const container = options.container ?? null;
  if (container) {
    const holder = itemOf(character, container);
    if (!holder) return blocked(character, "that container is not in the inventory");
  }
  if (options.stack !== false && STACKABLE_TYPES.has(entry.type)) {
    const existing = character.inventory.items.find((i) => i.uuid === entry.uuid && (i.container ?? null) === container && i.carryType !== "held" && !i.runes && !i.material && (i.size ?? "med") === (options.size ?? "med"));
    if (existing) return ok(replaceItem(character, { ...existing, quantity: existing.quantity + quantity }), existing.id);
  }
  const { inventory } = character;
  const id = `i${inventory.counter}`;
  const item: InventoryItem = { id, uuid: entry.uuid, quantity, ...defaultCarry(entry) };
  if (container) {
    item.container = container;
    item.carryType = options.stows === false ? "worn" : "stowed";
  }
  if (options.size && options.size !== "med" && options.size !== "sm" && entry.type !== "treasure") item.size = options.size;
  return ok(withInventory(character, { ...inventory, items: [...inventory.items, item], counter: inventory.counter + 1 }), id);
}

/** Add a kit's contents (Foundry: KitPF2e#dumpContents), nested containers included. The kit Entry's item rows name Entries by UUID. */
export function addKit(character: Character, kit: Entry, options: AddItemOptions = {}): InventoryChangeResult {
  const rows = isRecord(kit.system.items) ? kit.system.items : {};
  let next = character;
  const added: string[] = [];
  const walk = (list: Record<string, unknown>, container: string | null): void => {
    for (const row of Object.values(list)) {
      if (!isRecord(row) || typeof row.uuid !== "string") continue;
      const quantity = typeof row.quantity === "number" && row.quantity > 0 ? row.quantity : 1;
      const { inventory } = next;
      const id = `i${inventory.counter}`;
      const item: InventoryItem = { id, uuid: row.uuid, quantity, carryType: container ? "stowed" : "worn", handsHeld: 0 };
      if (container) item.container = container;
      next = withInventory(next, { ...inventory, items: [...inventory.items, item], counter: inventory.counter + 1 });
      added.push(id);
      if (row.isContainer === true && isRecord(row.items)) walk(row.items, id);
    }
  };
  walk(rows, options.container ?? null);
  if (added.length === 0) return blocked(character, `${kit.name} lists nothing to add`);
  return ok(next, added[0]);
}

/** Remove an item; what it held moves up to its container, or out. */
export function removeItem(character: Character, id: string): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  const items = character.inventory.items
    .filter((i) => i.id !== id)
    .map((i) => {
      const next = { ...i };
      if (next.container === id) {
        if (item.container) next.container = item.container;
        else {
          delete next.container;
          if (next.carryType === "stowed") next.carryType = "worn";
        }
      }
      if (next.ammo === id) delete next.ammo;
      return next;
    });
  return ok(withItems(character, items));
}

export function setQuantity(character: Character, id: string, quantity: number): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  const value = Math.max(0, Math.min(999999, Math.trunc(quantity)));
  if (!Number.isFinite(value)) return blocked(character, "a quantity is a whole number");
  return ok(replaceItem(character, { ...item, quantity: value }), id);
}

export interface CarryChange {
  carryType: CarryType;
  /** Hands to hold it in; a held item defaults to what its usage needs. */
  handsHeld?: 0 | 1 | 2;
}

/**
 * Change how an item is carried. Holding needs free hands (this item's own hands count as free),
 * and a two-handed weapon two of them. Dropping uninvests (Foundry does the same). Stowing an
 * item that sits in a container is the container's business: it leaves the container first.
 */
export function setCarry(character: Character, view: InventoryView | null, id: string, change: CarryChange): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  const facts = view?.items.find((i) => i.id === id);
  const usage = facts?.usage ?? null;
  const next: InventoryItem = { ...item, carryType: change.carryType, handsHeld: 0 };
  if (change.carryType === "held") {
    const hands = change.handsHeld ?? (usage?.type === "held" ? usage.hands : 1);
    if (hands < 1) return blocked(character, "holding an item takes at least one hand");
    if (usage?.type === "held" && hands < usage.hands) return blocked(character, `${facts?.name ?? "the item"} needs ${usage.hands} hands`);
    if (view) {
      const own = item.carryType === "held" ? item.handsHeld : 0;
      const free = view.hands.reallyFree + own;
      if (hands > free) return blocked(character, `${facts?.name ?? "the item"} needs ${hands} free hand${hands === 1 ? "" : "s"}; ${free === 0 ? "none is" : `only ${free} ${free === 1 ? "is" : "are"}`} free`);
    }
    next.handsHeld = hands;
    if (next.inSlot !== undefined) next.inSlot = false;
    if (next.container !== undefined) delete next.container;
  } else if (change.carryType === "dropped") {
    if (next.invested !== undefined) next.invested = false;
    if (next.inSlot !== undefined) next.inSlot = false;
    if (next.container !== undefined) delete next.container;
  } else if (change.carryType === "stowed") {
    if (next.inSlot !== undefined) next.inSlot = false;
    if (next.container !== undefined) delete next.container;
  }
  return ok(replaceItem(character, next), id);
}

/** Wear a slotted item in its slot (or take it out). Armour is exclusive: donning one takes the others off. */
export function setInSlot(character: Character, view: InventoryView | null, id: string, inSlot: boolean): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  const facts = view?.items.find((i) => i.id === id);
  if (facts && !(facts.usage.type === "worn" && facts.usage.where)) return blocked(character, `${facts.name} has no worn slot`);
  const worn: InventoryItem = { ...item, carryType: "worn", handsHeld: 0, inSlot };
  delete worn.container;
  let next = replaceItem(character, worn);
  if (inSlot && facts?.type === "armor") {
    next = withItems(next, next.inventory.items.map((i) => (i.id !== id && view?.items.find((v) => v.id === i.id)?.type === "armor" && i.inSlot ? { ...i, inSlot: false } : i)));
  }
  return ok(next, id);
}

/** Invest an item (Foundry: toggleInvested): it must be equipped, and the ten-item limit holds. */
export function setInvested(character: Character, view: InventoryView | null, id: string, invested: boolean): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  if (item.invested === undefined) return blocked(character, `${view?.items.find((i) => i.id === id)?.name ?? "the item"} takes no investiture`);
  if (invested && view) {
    const facts = view.items.find((i) => i.id === id);
    if (facts && !facts.equipped) return blocked(character, `${facts.name} must be worn (or held) to be invested`);
    if (view.investiture.value >= view.investiture.max && facts?.invested !== true) return blocked(character, `${view.investiture.max} items are already invested; uninvest one first`);
  }
  return ok(replaceItem(character, { ...item, invested }), id);
}

/** Put an item in a container (or take it out with null). A container cannot hold itself or an ancestor. */
export function moveToContainer(character: Character, view: InventoryView | null, id: string, container: string | null): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  if (container === null) {
    const next = { ...item };
    delete next.container;
    if (next.carryType === "stowed") next.carryType = "worn";
    return ok(replaceItem(character, next), id);
  }
  const holder = itemOf(character, container);
  if (!holder) return blocked(character, "that container is not in the inventory");
  const holderFacts = view?.items.find((i) => i.id === container);
  if (holderFacts && holderFacts.type !== "backpack") return blocked(character, `${holderFacts.name} is not a container`);
  if (container === id) return blocked(character, "a container cannot hold itself");
  for (let ancestor = holder.container; ancestor; ancestor = itemOf(character, ancestor)?.container) {
    if (ancestor === id) return blocked(character, "a container cannot go inside something it holds");
  }
  const stows = holderFacts ? holderFacts.stowsItems : true;
  const next: InventoryItem = { ...item, container, carryType: stows ? "stowed" : "worn", handsHeld: 0 };
  if (next.inSlot !== undefined) next.inSlot = false;
  return ok(replaceItem(character, next), id);
}

/**
 * Set the runes on a weapon, armour or shield. Potency, striking, resilient and reinforcing are
 * bounded; property runes must be ones the tables know for the item type, within the slots the
 * potency (and orichalcum) allow; a specific magic item keeps its own.
 */
export function setRunes(character: Character, view: InventoryView | null, id: string, runes: ItemRunes): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  const facts = view?.items.find((i) => i.id === id);
  const type = facts?.type ?? null;
  if (type && !["weapon", "armor", "shield"].includes(type)) return blocked(character, `${facts?.name ?? "the item"} takes no runes`);
  if (facts?.specific) return blocked(character, `${facts.name} is a specific magic item: its runes are its own`);
  const clampTo = (value: number | undefined, max: number, label: string): number | undefined | string => {
    if (value === undefined) return undefined;
    if (!Number.isInteger(value) || value < 0 || value > max) return `${label} is a whole number from 0 to ${max}`;
    return value;
  };
  const out: ItemRunes = {};
  const potency = clampTo(runes.potency, 4, "potency");
  if (typeof potency === "string") return blocked(character, potency);
  if (potency) out.potency = potency;
  if (type === "weapon" || type === null) {
    const striking = clampTo(runes.striking, 4, "striking");
    if (typeof striking === "string") return blocked(character, striking);
    if (striking) out.striking = striking;
  }
  if (type === "armor" || type === null) {
    const resilient = clampTo(runes.resilient, 4, "resilient");
    if (typeof resilient === "string") return blocked(character, resilient);
    if (resilient) out.resilient = resilient;
  }
  if (type === "shield" || type === null) {
    const reinforcing = clampTo(runes.reinforcing, 6, "reinforcing");
    if (typeof reinforcing === "string") return blocked(character, reinforcing);
    if (reinforcing) out.reinforcing = reinforcing;
  }
  if (runes.property && runes.property.length > 0) {
    if (type === "shield") return blocked(character, "a shield takes no property runes");
    const table = type === "armor" ? ARMOR_PROPERTY_RUNES : WEAPON_PROPERTY_RUNES;
    const unknown = runes.property.find((p) => !(p in table));
    if (unknown) return blocked(character, `"${unknown}" is not a ${type === "armor" ? "armour" : "weapon"} property rune`);
    const property = prunePropertyRunes(runes.property, table);
    if (property.length !== new Set(runes.property).size) return blocked(character, "a greater rune replaces its lesser version; list each rune once");
    const slots = propertyRuneSlots({ material: { type: item.material?.type ?? facts?.material.type ?? null }, runes: { potency: out.potency ?? 0 } });
    if (property.length > slots) return blocked(character, slots === 0 ? "property runes need a potency rune (or orichalcum) for a slot" : `only ${slots} property rune${slots === 1 ? "" : "s"} fit${slots === 1 ? "s" : ""}: one per potency, one more for orichalcum`);
    out.property = property;
  }
  const next = { ...item };
  if (Object.keys(out).length === 0) delete next.runes;
  else next.runes = out;
  return ok(replaceItem(character, next), id);
}

/** Set (or clear) a precious material; the grade must exist for the material and item type. */
export function setMaterial(character: Character, view: InventoryView | null, id: string, material: ItemMaterial | null): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  const facts = view?.items.find((i) => i.id === id);
  if (facts?.specific) return blocked(character, `${facts.name} is a specific magic item: its material is its own`);
  const next = { ...item };
  if (!material) {
    delete next.material;
    return ok(replaceItem(character, next), id);
  }
  if (!isMaterialGrade(material.grade)) return blocked(character, "a grade is low, standard or high");
  if (facts) {
    const like = { type: facts.type, baseType: null };
    if (!materialsFor(like).includes(material.type)) return blocked(character, `${facts.name} cannot be made of ${material.type}`);
    if (!materialGradesFor(like, material.type).includes(material.grade)) return blocked(character, `${material.type} does not come in ${material.grade} grade for ${facts.type === "armor" ? "armour" : `a ${facts.type}`}`);
  }
  next.material = { type: material.type, grade: material.grade };
  return ok(replaceItem(character, next), id);
}

export function setTraitToggles(character: Character, id: string, toggles: TraitToggles): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  const merged: TraitToggles = { ...(item.traitToggles ?? {}), ...toggles };
  const next = { ...item, traitToggles: merged };
  return ok(replaceItem(character, next), id);
}

/** Select the ammunition stack a weapon fires; the view says which stacks fit. */
export function setAmmunition(character: Character, view: InventoryView | null, weaponId: string, ammoId: string | null): InventoryChangeResult {
  const weapon = itemOf(character, weaponId);
  if (!weapon) return blocked(character, "no such item");
  const next = { ...weapon };
  if (ammoId === null) {
    delete next.ammo;
    return ok(replaceItem(character, next), weaponId);
  }
  if (!itemOf(character, ammoId)) return blocked(character, "that ammunition is not in the inventory");
  const facts = view?.items.find((i) => i.id === weaponId);
  if (facts && !facts.ammunition) return blocked(character, `${facts.name} takes no ammunition`);
  if (facts?.ammunition && !facts.ammunition.compatible.some((c) => c.id === ammoId)) return blocked(character, `${view?.items.find((i) => i.id === ammoId)?.name ?? "that"} does not fit ${facts.name}`);
  next.ammo = ammoId;
  return ok(replaceItem(character, next), weaponId);
}

/** Set an item's current Hit Points (a shield after Shield Block), bounded by its maximum. */
export function setItemHp(character: Character, view: InventoryView | null, id: string, hp: number): InventoryChangeResult {
  const item = itemOf(character, id);
  if (!item) return blocked(character, "no such item");
  const facts = view?.items.find((i) => i.id === id);
  if (facts && !facts.hp) return blocked(character, `${facts.name} has no Hit Points`);
  const max = facts?.hp?.max ?? Infinity;
  const value = Math.max(0, Math.min(max, Math.trunc(hp)));
  const next = { ...item };
  if (facts?.hp && value >= facts.hp.max) delete next.hp;
  else next.hp = value;
  return ok(replaceItem(character, next), id);
}

export function setCoins(character: Character, coins: RawCoins): InventoryChangeResult {
  const value = new Coins(coins);
  return ok(withInventory(character, { ...character.inventory, coins: value.toObject() }));
}

export function addCoins(character: Character, coins: RawCoins): InventoryChangeResult {
  const total = new Coins(character.inventory.coins).plus(coins);
  return ok(withInventory(character, { ...character.inventory, coins: total.toObject() }));
}

/**
 * Spend coins (Foundry: removeCurrency by value): exact denominations first, then larger coins
 * broken into smaller ones as change. Refused when the purse cannot cover the value.
 */
export function removeCoins(character: Character, coins: RawCoins): InventoryChangeResult {
  const purse = new Coins(character.inventory.coins);
  const cost = new Coins(coins);
  if (cost.copperValue > purse.copperValue) return blocked(character, `not enough coin: ${purse.toString()} against ${cost.toString()}`);
  const have: Record<"pp" | "gp" | "sp" | "cp", number> = { pp: purse.pp, gp: purse.gp, sp: purse.sp, cp: purse.cp };
  // Exact coins first.
  const rates = { pp: 1000, gp: 100, sp: 10, cp: 1 } as const;
  let remaining = cost.copperValue;
  for (const d of ["pp", "gp", "sp", "cp"] as const) {
    const take = Math.min(have[d], Math.floor(remaining / rates[d]), (coins[d] ?? 0));
    have[d] -= take;
    remaining -= take * rates[d];
  }
  // Then anything else, smallest coins first, breaking a larger coin when the small ones run out.
  const order = ["cp", "sp", "gp", "pp"] as const;
  while (remaining > 0) {
    let paid = false;
    for (const d of order) {
      if (have[d] > 0 && rates[d] <= remaining) {
        const take = Math.min(have[d], Math.floor(remaining / rates[d]));
        have[d] -= take;
        remaining -= take * rates[d];
        paid = true;
        break;
      }
    }
    if (paid) continue;
    // Break the smallest coin larger than what is left.
    const larger = order.find((d) => rates[d] > remaining && have[d] > 0);
    if (!larger) return blocked(character, "the purse cannot make change for that");
    have[larger] -= 1;
    const smaller = order[order.indexOf(larger) - 1]!;
    have[smaller] += rates[larger] / rates[smaller];
  }
  return ok(withInventory(character, { ...character.inventory, coins: new Coins(have).toObject() }));
}
